import { useState, useEffect, useRef } from 'react';

export function useTypingAnimation(
  text: string,
  speed: number = 10,
  enabled: boolean = true,
  delay: number = 500,
  startDelay: number = 0
) {
  const [displayedText, setDisplayedText] = useState("");
  const [isTypingDone, setIsTypingDone] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const enabledRef = useRef(enabled);
  const indexRef = useRef(0);
  const lastUpdateTimeRef = useRef(0);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    setDisplayedText("");
    setIsTypingDone(false);
    setHasStarted(false);
    indexRef.current = 0;
    lastUpdateTimeRef.current = 0;

    const startTimeout = setTimeout(() => {
      if (enabledRef.current) {
        setHasStarted(true);
        lastUpdateTimeRef.current = performance.now();
      }
    }, startDelay);

    return () => {
      clearTimeout(startTimeout);
    };
  }, [text, speed, delay, startDelay]);

  useEffect(() => {
    if (!hasStarted) return;

    const animate = (timestamp: number) => {
      if (!enabledRef.current) {
        requestAnimationFrame(animate);
        return;
      }

      if (timestamp - lastUpdateTimeRef.current >= speed) {
        if (indexRef.current < text.length) {
          setDisplayedText(text.slice(0, indexRef.current + 1));
          indexRef.current++;
          lastUpdateTimeRef.current = timestamp;
        } else {
          setIsTypingDone(true);
          return;
        }
      }

      if (indexRef.current < text.length) {
        requestAnimationFrame(animate);
      }
    };

    const animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [text, speed, delay, hasStarted]);

  useEffect(() => {
    if (enabled && !hasStarted) {
      setHasStarted(true);
      lastUpdateTimeRef.current = performance.now();
    }
  }, [enabled, hasStarted]);

  return displayedText;
}